import React, { useEffect, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { useI18n } from 'i18n'

// Must match the API (DATA_RETENTION in landed-cost-api/src/config/constants.js);
// only used to preview dates before the API answers with the real ones.
export const EXPORT_DAYS = 30
const TRIAL_NOTICE_DAYS = 30

// API timestamps are UTC; fmt.day reads the date part, so convert to the local
// calendar day first (otherwise an evening in Mexico shows tomorrow's date).
export const localDay = (value, plusDays = 0) => (value ? DateTime.fromISO(String(value)).plus({ days: plusDays }).toISODate() : null)

/**
 * Where a client is in its life: active → terminated (export window, data
 * deleted on purgeAfter) → purged (tombstone). Fields from GET /clients.
 */
export const clientState = (client) => {
  if (client.purgedAt) return 'purged'
  if (client.terminatedAt) return 'terminated'
  return 'active'
}

/**
 * Status cell of a client row, with the action that fits its state.
 */
export const ClientStatus = ({ client, busy, onTerminate, onReactivate }) => {
  const { t, fmt } = useI18n()
  const state = clientState(client)

  if (state === 'purged') {
    return (
      <div>
        <span className='rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500'>{t('clientLifecycle.purged')}</span>
        <p className='mt-1 text-xs text-slate-500'>
          {t(client.closedReason === 'trial_inactive' ? 'clientLifecycle.purgedInactive' : 'clientLifecycle.purgedOn', { date: fmt.day(localDay(client.purgedAt)) })}
        </p>
      </div>
    )
  }

  if (state === 'terminated') {
    return (
      <div>
        <span className='rounded-full bg-status-incomplete/10 px-2 py-0.5 text-xs font-semibold text-status-incomplete'>{t('clientLifecycle.terminated')}</span>
        <p className='mt-1 text-xs font-semibold text-status-incomplete'>{t('clientLifecycle.deletesOn', { date: fmt.day(localDay(client.purgeAfter)) })}</p>
        <p className='text-xs text-slate-500'>{t('clientLifecycle.canExport')}</p>
        <button type='button' onClick={() => onReactivate(client)} disabled={busy}
          className='mt-1 text-left text-xs font-semibold text-brand hover:underline disabled:opacity-50'>
          {t('clientLifecycle.reactivate')}
        </button>
      </div>
    )
  }

  return (
    <div>
      <span className='rounded-full bg-status-ready/10 px-2 py-0.5 text-xs font-semibold text-status-ready'>{t('clientLifecycle.active')}</span>
      {client.lastActivityAt && (
        <p className='mt-1 text-xs text-slate-500'>{t('clientLifecycle.lastActivity', { date: fmt.day(localDay(client.lastActivityAt)) })}</p>
      )}
      {client.inactivityNoticeSentAt && (
        <p className='text-xs font-semibold text-status-review'>
          {t('clientLifecycle.noticeSent', { date: fmt.day(localDay(client.inactivityNoticeSentAt, TRIAL_NOTICE_DAYS)) })}
        </p>
      )}
      <button type='button' onClick={() => onTerminate(client)} disabled={busy}
        className='mt-1 text-xs font-semibold text-status-incomplete hover:underline disabled:opacity-50'>
        {t('clientLifecycle.terminate')}
      </button>
    </div>
  )
}

/**
 * Confirmation before terminating a client. Terminating starts a countdown to a
 * permanent deletion, so the admin types the client's name to confirm.
 */
export const TerminateClientDialog = ({ client, busy, onConfirm, onClose }) => {
  const { t, fmt } = useI18n()
  const [typed, setTyped] = useState('')
  const input = useRef(null)

  useEffect(() => {
    if (!client) return
    setTyped('')
    input.current?.focus()
  }, [client])

  useEffect(() => {
    if (!client) return
    const onKeyDown = (event) => { if (event.key === 'Escape' && !busy) onClose() }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [client, busy, onClose])

  if (!client) return null
  const deleteOn = fmt.day(DateTime.now().plus({ days: EXPORT_DAYS }).toISODate())
  const matches = typed.trim() === client.name.trim()
  const submit = (event) => {
    event.preventDefault()
    if (matches && !busy) onConfirm(client)
  }

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4' onClick={() => !busy && onClose()}>
      <form role='dialog' aria-modal='true' aria-labelledby='terminate-title' onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className='w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl'>
        <h2 id='terminate-title' className='text-lg font-bold text-slate-800'>{t('clientLifecycle.dialogTitle', { name: client.name })}</h2>
        <ul className='mt-3 space-y-2 text-sm text-slate-600'>
          <li>• {t('clientLifecycle.dialogExport', { days: EXPORT_DAYS, date: deleteOn })}</li>
          <li className='font-semibold text-status-incomplete'>• {t('clientLifecycle.dialogDelete', { date: deleteOn })}</li>
          <li>• {t('clientLifecycle.dialogUndo')}</li>
          <li>• {t('clientLifecycle.dialogKeep')}</li>
        </ul>
        <label htmlFor='terminate-confirm' className='mt-5 block text-sm text-slate-700'>
          {t('clientLifecycle.dialogTypeName', { name: client.name })}
        </label>
        <input id='terminate-confirm' ref={input} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete='off'
          className='mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-status-incomplete focus:outline-none' />
        <div className='mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
          <button type='button' onClick={onClose} disabled={busy}
            className='rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50'>
            {t('common.cancel')}
          </button>
          <button type='submit' disabled={!matches || busy}
            className='rounded-lg bg-status-incomplete px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40'>
            {busy ? t('common.saving') : t('clientLifecycle.dialogConfirm')}
          </button>
        </div>
      </form>
    </div>
  )
}
